import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { JobTypeEnum } from 'server/shared/models/jobTypeEnum';
import { queryParam$ } from '../../../helpers/queryParam$';

@Component({
  selector: 'app-job-type-multiselect',
  templateUrl: './job-type-multiselect.component.html',
  styleUrls: ['./job-type-multiselect.component.scss'],
})
export class JobTypeMultiselectComponent implements OnInit, OnDestroy {
  private destroyed$ = new Subject<void>();
  private suppressParamSync = false;

  @Input() useQueryParam?: string;
  @Input() label?: string;
  @Input() disabled = false;
  @Input() lightFormField = false;

  @Output() selectionChanged = new EventEmitter<JobTypeEnum[] | null>();

  jobTypeEnum = JobTypeEnum;
  selectedJobTypes: (JobTypeEnum | 'ALL')[] = [];

  constructor(private route: ActivatedRoute, private router: Router) {}

  ngOnInit(): void {
    if (this.useQueryParam) {
      queryParam$(this.route, this.useQueryParam)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((param) => {
          if (!this.suppressParamSync) {
            if (param) {
              this.selectedJobTypes = param
                .split(',')
                .map((jobType) => {
                  if (jobType === 'ALL') return 'ALL';
                  return JobTypeEnum[jobType as keyof typeof JobTypeEnum];
                })
                .filter((jobType) => jobType !== undefined);
            } else {
              // Default to ALL selected
              this.selectedJobTypes = ['ALL'];
            }
          }
        });
    } else {
      // Default to ALL selected
      this.selectedJobTypes = ['ALL'];
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onSelectionChange(selectedJobTypes: (JobTypeEnum | 'ALL')[]) {
    this.suppressParamSync = true;

    // If 'ALL' is currently selected and user selects a specific, remove 'ALL'
    if (this.selectedJobTypes.includes('ALL') && selectedJobTypes.length > 1) {
      selectedJobTypes = selectedJobTypes.filter((s) => s !== 'ALL');
    }

    // Mutually exclusive logic for 'ALL'
    if (selectedJobTypes.includes('ALL')) {
      this.selectedJobTypes = ['ALL'];
    } else {
      this.selectedJobTypes = selectedJobTypes.filter((s) => s !== 'ALL');
    }

    if (this.useQueryParam) {
      const paramValue =
        this.selectedJobTypes.length > 0
          ? this.selectedJobTypes
              .map((jobType) =>
                jobType === 'ALL' ? 'ALL' : JobTypeEnum[jobType as JobTypeEnum],
              )
              .join(',')
          : null;
      this.updateQueryParam(paramValue);
    }

    // Emit change
    const emitValue = this.selectedJobTypes.includes('ALL')
      ? null
      : this.selectedJobTypes.filter((jt): jt is JobTypeEnum => jt !== 'ALL');
    this.selectionChanged.emit(emitValue?.length ? emitValue : null);

    setTimeout(() => {
      this.suppressParamSync = false;
    }, 0);
  }

  private updateQueryParam(paramValue: string | null) {
    const queryParams: any = {};
    queryParams[this.useQueryParam || ''] = paramValue;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }
}
